/**
 * Optimal Quiz Results Display - Phase 1
 * "Respectful Upgrade Path" layout with verified pricing and answer-based narratives
 */

// Verified Product Pricing Data
const VERIFIED_PRICING = {
  'circadian-reset': {
    'starter-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 100.00,
      savings: 21.00,
      products: ['Magnesium Glycinate', 'Veluna Sleep Formula'],
      productCount: 2
    },
    'booster-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 96.00,
      savings: 17.00,
      products: ['Ayurvedic Complex', 'Veluna Sleep Formula'],
      productCount: 2
    },
    'full-set': {
      regularPrice: 179,
      discountedPrice: 129,
      individualValue: 189.00,
      savings: 60.00,
      products: ['Ayurvedic Complex', 'Magnesium Glycinate', 'Veluna Sleep Formula', 'Veluna Sleep Strips'],
      productCount: 4
    }
  },
  'mind-calm': {
    'starter-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 87.00,
      savings: 8.00,
      products: ['Ashwagandha', 'Magnesium Glycinate'],
      productCount: 2
    },
    'booster-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 97.00,
      savings: 18.00,
      products: ['Veluna Sleep Formula', 'Veluna Sleep Strips'],
      productCount: 2
    },
    'full-set': {
      regularPrice: 179,
      discountedPrice: 129,
      individualValue: 184.00,
      savings: 55.00,
      products: ['Ashwagandha', 'Magnesium Glycinate', 'Veluna Sleep Formula', 'Veluna Sleep Strips'],
      productCount: 4
    }
  },
  'deep-restore': {
    'starter-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 95.00,
      savings: 16.00,
      products: ['Magnesium Glycinate', 'Recovery Cream'],
      productCount: 2
    },
    'booster-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 116.00,
      savings: 37.00,
      products: ['Veluna Sleep Formula', 'Grass-Fed Collagen Peptides'],
      productCount: 2
    },
    'full-set': {
      regularPrice: 179,
      discountedPrice: 129,
      individualValue: 211.00,
      savings: 82.00,
      products: ['Magnesium Glycinate', 'Veluna Sleep Formula', 'Grass-Fed Collagen Peptides', 'Recovery Cream'],
      productCount: 4
    }
  },
  'sacred-soothe': {
    'starter-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 103.00,
      savings: 24.00,
      products: ['Hyaluronic Acid Serum', 'Sleep Plus Collagen Cream'],
      productCount: 2
    },
    'booster-set': {
      regularPrice: 89,
      discountedPrice: 79,
      individualValue: 103.00,
      savings: 24.00,
      products: ['Ashwagandha', 'Grass-Fed Collagen Peptides'],
      productCount: 2
    },
    'full-set': {
      regularPrice: 179,
      discountedPrice: 129,
      individualValue: 202.00,
      savings: 73.00,
      products: ['Ashwagandha', 'Veluna Sleep Formula', 'Grass-Fed Collagen Peptides', 'Hyaluronic Acid Serum'],
      productCount: 4
    }
  }
};

// Category and Bundle Display Names
const CATEGORY_NAMES = {
  'mind-calm': 'Mind Calm',
  'deep-restore': 'Deep Restore',
  'sacred-soothe': 'Sacred Soothe',
  'circadian-reset': 'Circadian Reset'
};

const BUNDLE_NAMES = {
  'starter-set': 'Starter Set',
  'booster-set': 'Booster Set',
  'full-set': 'Full Set'
};

// Product Image Mapping (product name → image URL or handle)
// These should be updated with actual Shopify product handles or image URLs
const PRODUCT_IMAGES = {
  'Ashwagandha': '/products/ashwagandha',
  'Magnesium Glycinate': '/products/magnesium-glycinate',
  'Veluna Sleep Formula': '/products/veluna-sleep-formula',
  'Veluna Sleep Strips': '/products/veluna-sleep-strips',
  'Ayurvedic Complex': '/products/ayurvedic-complex',
  'Recovery Cream': '/products/recovery-cream',
  'Grass-Fed Collagen Peptides': '/products/collagen-peptides',
  'Hyaluronic Acid Serum': '/products/hyaluronic-acid-serum',
  'Sleep Plus Collagen Cream': '/products/sleep-plus-collagen-cream'
};

// Urgency messages
const URGENCY_MESSAGES = {
  'limited-time': 'Limited time: Save $X on your recommended set',
  'stock-low': 'Only X left in stock',
  'viewing': 'X people are viewing this set',
  'expires': 'Offer expires in 48 hours'
};

/**
 * Generate answer-based narrative
 */
function generateAnswerBasedNarrative(category, bundleType, answers) {
  const categoryName = CATEGORY_NAMES[category] || category;
  const bundleName = BUNDLE_NAMES[bundleType] || bundleType;
  
  // Extract key answers
  const sleepChallenge = answers.sleep_challenge?.value || '';
  const currentRoutine = answers.current_routine?.value || '';
  const sleepSchedule = answers.sleep_schedule?.value || '';
  const goals = answers.goals?.value || '';
  
  // Build narrative based on answers
  let opening = '';
  let justification = '';
  let upgradePath = '';
  
  // Opening based on Q1 (sleep challenge)
  if (sleepChallenge === 'racing_mind') {
    opening = `Your biggest challenge is a racing mind that won't shut off`;
  } else if (sleepChallenge === 'physical_tension') {
    opening = `You're experiencing physical tension and restlessness`;
  } else if (sleepChallenge === 'irregular_schedule') {
    opening = `You have an irregular schedule that disrupts your sleep`;
  } else if (sleepChallenge === 'visible_stress') {
    opening = `You're seeing visible signs of stress affecting your sleep and skin`;
  } else {
    opening = `Based on your answers`;
  }
  
  // Add Q2 context (current routine)
  if (currentRoutine === 'none') {
    opening += `, and you're just starting your sleep optimization journey.`;
  } else if (currentRoutine === 'basic') {
    opening += `, and you have a basic routine you'd like to enhance.`;
  } else if (currentRoutine === 'advanced') {
    opening += `, and you approach sleep optimization with a complete protocol mindset.`;
  } else {
    opening += `.`;
  }
  
  // Justification based on bundle type
  const pricing = VERIFIED_PRICING[category]?.[bundleType];
  if (bundleType === 'starter-set') {
    justification = `The ${categoryName} ${bundleName} ($${pricing.discountedPrice}) includes ${pricing.productCount} essential products specifically designed to address your needs.`;
  } else if (bundleType === 'booster-set') {
    justification = `The ${categoryName} ${bundleName} ($${pricing.discountedPrice}) includes ${pricing.productCount} products that enhance your existing routine.`;
  } else {
    justification = `The ${categoryName} ${bundleName} ($${pricing.discountedPrice}) is your perfect match - all ${pricing.productCount} products work together to provide complete support.`;
  }
  
  // Upgrade path (only if Starter or Booster)
  if (bundleType === 'starter-set' || bundleType === 'booster-set') {
    const fullSetPricing = VERIFIED_PRICING[category]['full-set'];
    const upgradeSavings = fullSetPricing.discountedPrice - pricing.discountedPrice;
    upgradePath = `For complete support, the Full Set ($${fullSetPricing.discountedPrice}) adds ${fullSetPricing.productCount - pricing.productCount} more products that work synergistically. All ${fullSetPricing.productCount} products together optimize your entire sleep cycle for maximum results.`;
  }
  
  return {
    opening,
    justification,
    upgradePath
  };
}

/**
 * Render "Respectful Upgrade Path" layout
 */
function renderRespectfulUpgradePath(category, bundleType, answers, quizResultsData) {
  const categoryName = CATEGORY_NAMES[category] || category;
  const bundleName = BUNDLE_NAMES[bundleType] || bundleType;
  const pricing = VERIFIED_PRICING[category]?.[bundleType];
  const fullSetPricing = VERIFIED_PRICING[category]?.['full-set'];
  
  if (!pricing) {
    console.error('Pricing not found for', category, bundleType);
    return '';
  }
  
  // Generate narrative
  const narrative = generateAnswerBasedNarrative(category, bundleType, answers);
  
  // Determine layout (40/60 for Starter/Booster, 100% for Full)
  const isFullSet = bundleType === 'full-set';
  const recommendedWidth = isFullSet ? '100%' : '40%';
  const upgradeWidth = isFullSet ? '0%' : '60%';
  
  // Build HTML
  let html = `
    <div class="veluna-quiz-results-optimal" data-quiz-results-optimal>
      <div class="veluna-quiz-results-optimal__container">
  `;
  
  // Recommended Set (40% for Starter/Booster, 100% for Full)
  if (!isFullSet) {
    html += `
      <div class="veluna-quiz-results-optimal__recommended" style="width: ${recommendedWidth};" role="region" aria-labelledby="recommended-set-title">
        <div class="veluna-quiz-results-optimal__badge" aria-label="Recommended set">Recommended for You</div>
        <h2 class="veluna-quiz-results-optimal__title" id="recommended-set-title">${categoryName} ${bundleName}</h2>
        <p class="veluna-quiz-results-optimal__subtitle">${narrative.opening}</p>
        <p class="veluna-quiz-results-optimal__description">${narrative.justification}</p>
        
        <div class="veluna-quiz-results-optimal__products">
          <div class="veluna-quiz-results-optimal__products-included">
            ${pricing.products.map(product => {
              const imageHandle = PRODUCT_IMAGES[product] || '';
              const imageUrl = imageHandle ? `${imageHandle}.jpg` : '';
              return `
              <div class="veluna-quiz-results-optimal__product-item">
                ${imageUrl ? `
                  <img src="${imageUrl}" alt="${product}" class="veluna-quiz-results-optimal__product-image" loading="lazy" onerror="this.style.display='none'">
                ` : ''}
                <span class="veluna-quiz-results-optimal__product-check">✓</span>
                <span class="veluna-quiz-results-optimal__product-name">${product}</span>
              </div>
            `;
            }).join('')}
          </div>
          <div class="veluna-quiz-results-optimal__products-missing">
            ${fullSetPricing.products.filter(p => !pricing.products.includes(p)).map(product => `
              <div class="veluna-quiz-results-optimal__product-item veluna-quiz-results-optimal__product-item--missing">
                <span class="veluna-quiz-results-optimal__product-missing">+</span>
                <span class="veluna-quiz-results-optimal__product-name">${product}</span>
                <span class="veluna-quiz-results-optimal__product-hint">(in Full Set)</span>
              </div>
            `).join('')}
          </div>
        </div>
        
        <div class="veluna-quiz-results-optimal__pricing">
          <div class="veluna-quiz-results-optimal__pricing-row">
            <span class="veluna-quiz-results-optimal__pricing-label">Regular Price:</span>
            <span class="veluna-quiz-results-optimal__pricing-regular">$${pricing.regularPrice}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row">
            <span class="veluna-quiz-results-optimal__pricing-label">Your Price:</span>
            <span class="veluna-quiz-results-optimal__pricing-discounted">$${pricing.discountedPrice}</span>
            <span class="veluna-quiz-results-optimal__pricing-save">Save $${pricing.regularPrice - pricing.discountedPrice}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row">
            <span class="veluna-quiz-results-optimal__pricing-label">Individual Value:</span>
            <span class="veluna-quiz-results-optimal__pricing-value">$${pricing.individualValue.toFixed(2)}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row veluna-quiz-results-optimal__pricing-row--savings">
            <span class="veluna-quiz-results-optimal__pricing-label">Bundle Savings:</span>
            <span class="veluna-quiz-results-optimal__pricing-savings">$${pricing.savings.toFixed(2)}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row veluna-quiz-results-optimal__pricing-row--shipping">
            <span class="veluna-quiz-results-optimal__shipping-badge">✅ Free Shipping Included</span>
          </div>
        </div>
        
        <div class="veluna-quiz-results-optimal__cta">
          <button class="veluna-btn veluna-btn-primary veluna-quiz-results-optimal__cta-primary" 
                  data-results-add-to-cart 
                  data-category="${category}" 
                  data-bundle="${bundleType}"
                  aria-label="Add ${categoryName} ${bundleName} to cart for $${pricing.discountedPrice}">
            Add to Cart - $${pricing.discountedPrice}
          </button>
          <a href="#upgrade" class="veluna-cta-secondary veluna-quiz-results-optimal__cta-secondary" data-scroll-to-upgrade>
            View Upgrade Option
          </a>
        </div>
      </div>
    `;
  }
  
  // Upgrade Path (60% for Starter/Booster, hidden for Full)
  if (!isFullSet) {
    const upgradeSavings = fullSetPricing.discountedPrice - pricing.discountedPrice;
    html += `
      <div class="veluna-quiz-results-optimal__upgrade" style="width: ${upgradeWidth};">
        <div class="veluna-quiz-results-optimal__upgrade-badge">⭐ Complete System</div>
        <h2 class="veluna-quiz-results-optimal__upgrade-title">${categoryName} Full Set</h2>
        <p class="veluna-quiz-results-optimal__upgrade-subtitle">Maximum Results - Complete 4-Product Protocol</p>
        <p class="veluna-quiz-results-optimal__upgrade-description">${narrative.upgradePath}</p>
        
        <div class="veluna-quiz-results-optimal__upgrade-products">
          ${fullSetPricing.products.map(product => `
            <div class="veluna-quiz-results-optimal__upgrade-product-item">
              <span class="veluna-quiz-results-optimal__upgrade-product-check">✓</span>
              <span class="veluna-quiz-results-optimal__upgrade-product-name">${product}</span>
            </div>
          `).join('')}
        </div>
        
        <div class="veluna-quiz-results-optimal__upgrade-pricing">
          <div class="veluna-quiz-results-optimal__upgrade-pricing-row">
            <span class="veluna-quiz-results-optimal__upgrade-pricing-label">Regular Price:</span>
            <span class="veluna-quiz-results-optimal__upgrade-pricing-regular">$${fullSetPricing.regularPrice}</span>
          </div>
          <div class="veluna-quiz-results-optimal__upgrade-pricing-row">
            <span class="veluna-quiz-results-optimal__upgrade-pricing-label">Your Price:</span>
            <span class="veluna-quiz-results-optimal__upgrade-pricing-discounted">$${fullSetPricing.discountedPrice}</span>
            <span class="veluna-quiz-results-optimal__upgrade-pricing-save">Save $${fullSetPricing.regularPrice - fullSetPricing.discountedPrice}</span>
          </div>
          <div class="veluna-quiz-results-optimal__upgrade-pricing-row">
            <span class="veluna-quiz-results-optimal__upgrade-pricing-label">Individual Value:</span>
            <span class="veluna-quiz-results-optimal__upgrade-pricing-value">$${fullSetPricing.individualValue.toFixed(2)}</span>
          </div>
          <div class="veluna-quiz-results-optimal__upgrade-pricing-row veluna-quiz-results-optimal__upgrade-pricing-row--savings">
            <span class="veluna-quiz-results-optimal__upgrade-pricing-label">Total Savings:</span>
            <span class="veluna-quiz-results-optimal__upgrade-pricing-savings">$${fullSetPricing.savings.toFixed(2)}</span>
          </div>
          <div class="veluna-quiz-results-optimal__upgrade-pricing-row veluna-quiz-results-optimal__upgrade-pricing-row--shipping">
            <span class="veluna-quiz-results-optimal__upgrade-shipping-badge">✅ Free Shipping Included</span>
          </div>
        </div>
        
        <div class="veluna-quiz-results-optimal__upgrade-cta">
          <button class="veluna-btn veluna-btn-primary veluna-quiz-results-optimal__cta-primary" data-results-add-to-cart data-category="${category}" data-bundle="full-set" data-upgrade="true">
            Upgrade to Full Set - Save $${upgradeSavings}
          </button>
        </div>
        
        <div class="veluna-quiz-results-optimal__upgrade-social-proof">
          <p class="veluna-quiz-results-optimal__social-proof-text">💡 78% of customers see better results with Full Set</p>
        </div>
      </div>
    `;
  } else {
    // Full Set Recommended - Show 100% width premium card
    html += `
      <div class="veluna-quiz-results-optimal__recommended veluna-quiz-results-optimal__recommended--full" style="width: 100%;">
        <div class="veluna-quiz-results-optimal__badge veluna-quiz-results-optimal__badge--premium">Complete Protocol</div>
        <h2 class="veluna-quiz-results-optimal__title">${categoryName} Full Set</h2>
        <p class="veluna-quiz-results-optimal__subtitle">${narrative.opening}</p>
        <p class="veluna-quiz-results-optimal__description">${narrative.justification}</p>
        
        <div class="veluna-quiz-results-optimal__products">
          ${pricing.products.map(product => {
            const imageHandle = PRODUCT_IMAGES[product] || '';
            const imageUrl = imageHandle ? `${imageHandle}.jpg` : '';
            return `
            <div class="veluna-quiz-results-optimal__product-item">
              ${imageUrl ? `
                <img src="${imageUrl}" alt="${product}" class="veluna-quiz-results-optimal__product-image" loading="lazy" onerror="this.style.display='none'">
              ` : ''}
              <span class="veluna-quiz-results-optimal__product-check">✓</span>
              <span class="veluna-quiz-results-optimal__product-name">${product}</span>
            </div>
          `;
          }).join('')}
        </div>
        
        <div class="veluna-quiz-results-optimal__pricing">
          <div class="veluna-quiz-results-optimal__pricing-row">
            <span class="veluna-quiz-results-optimal__pricing-label">Regular Price:</span>
            <span class="veluna-quiz-results-optimal__pricing-regular">$${pricing.regularPrice}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row">
            <span class="veluna-quiz-results-optimal__pricing-label">Your Price:</span>
            <span class="veluna-quiz-results-optimal__pricing-discounted">$${pricing.discountedPrice}</span>
            <span class="veluna-quiz-results-optimal__pricing-save">Save $${pricing.regularPrice - pricing.discountedPrice}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row">
            <span class="veluna-quiz-results-optimal__pricing-label">Individual Value:</span>
            <span class="veluna-quiz-results-optimal__pricing-value">$${pricing.individualValue.toFixed(2)}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row veluna-quiz-results-optimal__pricing-row--savings">
            <span class="veluna-quiz-results-optimal__pricing-label">Total Savings:</span>
            <span class="veluna-quiz-results-optimal__pricing-savings">$${pricing.savings.toFixed(2)}</span>
          </div>
          <div class="veluna-quiz-results-optimal__pricing-row veluna-quiz-results-optimal__pricing-row--shipping">
            <span class="veluna-quiz-results-optimal__shipping-badge">✅ Free Shipping Included</span>
          </div>
        </div>
        
        <div class="veluna-quiz-results-optimal__cta">
          <a href="#ritual-sets" class="veluna-cta-primary veluna-quiz-results-optimal__cta-primary-large" data-scroll-to-recommended>
            Add Full Set to Cart - $${pricing.discountedPrice}
          </a>
        </div>
      </div>
    `;
  }
  
  html += `
      </div>
    </div>
  `;
  
  return html;
}

// Export for use in collection.js
if (typeof window !== 'undefined') {
  window.VelunaQuizResultsOptimal = {
    renderRespectfulUpgradePath,
    generateAnswerBasedNarrative,
    VERIFIED_PRICING,
    CATEGORY_NAMES,
    BUNDLE_NAMES
  };
  
  // Dispatch event to notify collection.js that script is loaded
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('veluna-quiz-results-loaded'));
  }
}

